/**
 * メンバー1人の画面（issue #89・docs/01 15-1）。
 *
 * ここでできること:
 *  - ログインのしかたの確認（ご本人がアプリで名簿の電話番号を入れてログインする・決定33。
 *    招待リンクは使わなくなったので、発行の操作は置かない）
 *  - アカウントの停止（無効化）と、停止した方を名簿へ戻すこと
 *  - 役職・拠点の変更（受け持ちが全社の方のみ・docs/01 15-1）
 *  - ふりがなを直す（CSV の取り込みで更新できる範囲と同じ・issue #521）
 *
 * 決めごと（docs/06 原則1・2・4）:
 *  - 変更は**「保存」を押したときだけ**。押したら「保存しました」を必ず出す
 *  - 止める・作り直すなど、元に戻しにくい操作の前には**確認の窓**をはさみ、終わったら結果を出す
 *  - できないことは、押せないボタンではなく**理由の言葉**でお伝えする
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApi } from '../api/context.js';
import type { MemberChoices, MemberDetail } from '../api/types.js';
import { useSession } from '../auth/session-context.js';
import { employmentTypeLabel, memberStatusLabel, memberStatusNote } from '../members/labels.js';
import { ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { loadFailure, type LoadFailure, nextStepFor } from '../api/load-failure.js';
import { formatPhone } from '../format/phone.js';
import { endsWithRequest, withReason } from '../format/sentence.js';
import { can } from '../menu/visible.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { FormCard } from '../components/ui/FormCard.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { ROLE_REQUIREMENTS, useRoleWording, type RoleWording } from '../auth/role-wording.js';

/** いま確認の窓を出している操作。 */
type Confirming = 'none' | 'deactivate' | 'reactivate' | 'assignment';

export function MemberDetailPage(): ReactNode {
  const api = useApi();
  const { id = '' } = useParams();
  const { profile } = useSession();
  // 案内に出す役職の呼び名（その会社の呼び名・issue #480）
  const wording = useRoleWording();
  const [member, setMember] = useState<MemberDetail | null>(null);
  const [choices, setChoices] = useState<MemberChoices | null>(null);
  /** 選択肢が読めなかった理由（読めたときは null） */
  const [choicesError, setChoicesError] = useState<string | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // 役職・拠点の入力（保存を押すまで名簿は変わりません）
  const [role, setRole] = useState('');
  const [siteId, setSiteId] = useState('');
  const [managedSiteIds, setManagedSiteIds] = useState<string[]>([]);
  const [savingManagedSites, setSavingManagedSites] = useState(false);
  const [managedSitesResult, setManagedSitesResult] = useState<Banner | null>(null);
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [assignmentResult, setAssignmentResult] = useState<Banner | null>(null);

  // ふりがなの入力（保存を押すまで名簿は変わりません・issue #521）
  const [nameKana, setNameKana] = useState('');
  const [savingNameKana, setSavingNameKana] = useState(false);
  const [nameKanaResult, setNameKanaResult] = useState<Banner | null>(null);

  const [confirming, setConfirming] = useState<Confirming>('none');
  const [busy, setBusy] = useState(false);
  const [statusResult, setStatusResult] = useState<Banner | null>(null);

  useEffect(() => {
    let cancelled = false;
    setMember(null);
    setError(null);
    api
      .get<MemberDetail>(`/admin/members/${id}`)
      .then((loaded) => {
        if (cancelled) return;
        setMember(loaded);
        setRole(loaded.role);
        setSiteId(loaded.site_id ?? '');
        setManagedSiteIds((loaded.managed_sites ?? []).map((site) => site.id));
        setNameKana(loaded.name_kana ?? '');
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(loadFailure(caught));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, id, reloadKey]);

  useEffect(() => {
    let cancelled = false;
    api
      .get<MemberChoices>('/admin/members/choices')
      .then((loaded) => {
        if (cancelled) return;
        setChoices(loaded);
        setChoicesError(null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // 黙って空の選択肢にしない。空のまま「保存」を出すと、選べないのに押せて、
        // 何も変わっていないのに「保存しました」と出てしまう
        setChoices(null);
        setChoicesError(
          e instanceof Error && e.message !== ''
            ? e.message
            : '役職と拠点の選択肢を読み込めませんでした。',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const apply = useCallback((updated: MemberDetail) => {
    setMember(updated);
    setRole(updated.role);
    setSiteId(updated.site_id ?? '');
    setManagedSiteIds((updated.managed_sites ?? []).map((site) => site.id));
    setNameKana(updated.name_kana ?? '');
  }, []);

  async function saveManagedSites(): Promise<void> {
    if (!member) return;
    setSavingManagedSites(true);
    setManagedSitesResult(null);
    try {
      const updated = await api.post<MemberDetail>(`/admin/users/${member.id}/managed-sites`, {
        body: { site_ids: managedSiteIds },
      });
      apply(updated);
      setManagedSitesResult({
        tone: 'success',
        message: `保存しました。所属拠点：${updated.site_name ?? 'ありません'}／受け持つ拠点：${(updated.managed_sites ?? []).map((site) => site.name).join('・') || 'ありません'}`,
      });
    } catch (caught) {
      setManagedSitesResult(failureBanner(caught, '受け持つ拠点を保存できませんでした。'));
    } finally {
      setSavingManagedSites(false);
    }
  }

  async function saveNameKana(): Promise<void> {
    if (!member) return;
    setSavingNameKana(true);
    setNameKanaResult(null);
    try {
      const updated = await api.post<MemberDetail>(`/admin/users/${member.id}/name-kana`, {
        body: { name_kana: nameKana.trim() === '' ? null : nameKana },
      });
      apply(updated);
      setNameKanaResult({
        tone: 'success',
        message:
          updated.name_kana == null
            ? 'ふりがなを消しました。'
            : `保存しました（ふりがな：${updated.name_kana}）。`,
      });
    } catch (caught) {
      setNameKanaResult(failureBanner(caught, 'ふりがなを保存できませんでした。'));
    } finally {
      setSavingNameKana(false);
    }
  }

  async function saveAssignment(): Promise<void> {
    if (!member) return;
    setSavingAssignment(true);
    setAssignmentResult(null);
    try {
      const updated = await api.post<MemberDetail>(`/admin/users/${member.id}/assignment`, {
        body: { role, site_id: siteId === '' ? null : siteId },
      });
      apply(updated);
      setAssignmentResult({
        tone: 'success',
        message: `保存しました（役職：${updated.role_display_name}／拠点：${
          updated.site_name ?? '決まっていません'
        }）。`,
      });
    } catch (caught) {
      setAssignmentResult(failureBanner(caught, '役職・拠点を保存できませんでした。'));
    } finally {
      setSavingAssignment(false);
      setConfirming('none');
    }
  }

  async function changeStatus(action: 'deactivate' | 'reactivate'): Promise<void> {
    if (!member) return;
    setBusy(true);
    setStatusResult(null);
    try {
      if (action === 'deactivate') {
        await api.post(`/admin/users/${member.id}/deactivate`);
        setStatusResult({
          tone: 'success',
          message: `${member.display_name}さんのアカウントを無効にしました。ログインできなくなります。`,
        });
        setReloadKey((n) => n + 1);
      } else {
        const updated = await api.post<MemberDetail>(`/admin/users/${member.id}/reactivate`);
        apply(updated);
        setStatusResult({
          tone: 'success',
          message: `${member.display_name}さんの無効を解除しました。在籍状態は「ログイン前」です。`,
          nextStep:
            'ご本人がアプリを開いて、メンバーに登録された電話番号を入れると、またログインできます。',
        });
      }
    } catch (caught) {
      setStatusResult(
        failureBanner(
          caught,
          action === 'deactivate'
            ? 'アカウントを無効にできませんでした。'
            : '無効を解除できませんでした。',
        ),
      );
    } finally {
      setBusy(false);
      setConfirming('none');
    }
  }

  if (error) {
    return (
      <article>
        <PageHeader title="メンバー" />
        <ErrorBlock failure={error} onRetry={() => setReloadKey((n) => n + 1)} />
        <p>
          <Link to="/members">メンバーの一覧に戻る</Link>
        </p>
      </article>
    );
  }
  if (!member) {
    return (
      <article>
        <PageHeader title="メンバー" />
        <LoadingBlock label="この方の情報を読み込んでいます…" />
      </article>
    );
  }

  const isSelf = profile?.me.user_id === member.id;
  // ご自身が、全社の名簿を扱える役職か（案内の「ほかの◯◯の方へ」に使う）
  const canAssignOthers = profile?.grant.scope === 'org' && can(profile.grant, 'member.manage');
  // ご自身の役職・拠点は変えられない（サーバも断る）。押せるのに必ず断られるボタンは出さない
  const canAssign = profile?.grant.scope === 'org' && !isSelf;
  const isDeactivated = member.status === 'deactivated';
  // 「無効にする」「無効を解除する」ができないときの理由（サーバの門番と同じ判定・issue #373）
  const statusBlockedReason = member.status_change_blocked_reason ?? null;
  const assignmentChanged = role !== member.role || siteId !== (member.site_id ?? '');
  // この方の役職に、ご自身が持っていないできることが含まれるか（含まれれば役職・拠点は変えられない・#451）
  const roleBeyondOwn = member.role_beyond_own_capabilities ?? [];
  // 付けられない役職（ご自身が持っていないできることを含む・#451）。選べない表示にして理由を添える
  const unassignableRoles = (choices?.assignable_roles ?? []).filter(
    (option) => (option.beyond_own_capabilities ?? []).length > 0 && option.key !== member.role,
  );
  // ふりがなを直せないときの理由（サーバの門番と同じ判定・issue #521）
  const nameKanaBlockedReason = member.name_kana_blocked_reason ?? null;
  const nameKanaChanged = nameKana.trim() !== (member.name_kana ?? '');
  // CSVで電話番号を登録できる方か（できない方には、頼み先をお伝えする）
  const canImport = profile ? can(profile.grant, 'roster.manage') : false;

  return (
    <article>
      <PageHeader
        title={`${member.display_name}さん`}
        description={`${member.role_display_name}・${member.site_name ?? '拠点が決まっていません'}・${memberStatusLabel(member.status)}`}
        actions={
          <Link className="ui-button ui-button-secondary" to="/members">
            メンバーの一覧に戻る
          </Link>
        }
      />

      <Card title="この方の情報">
        <dl className="admin-facts">
          <dt>ふりがな</dt>
          <dd>{member.name_kana ?? NOT_REGISTERED}</dd>
          <dt>在籍状態</dt>
          <dd>
            {memberStatusLabel(member.status)}
            <small>{memberStatusNote(member.status)}</small>
          </dd>
          <dt>電話番号</dt>
          <dd>{member.phone === null ? NOT_REGISTERED : formatPhone(member.phone)}</dd>
          <dt>メールアドレス</dt>
          <dd>{member.email ?? NOT_REGISTERED}</dd>
          <dt>スタッフID</dt>
          <dd>{member.staff_code ?? NOT_REGISTERED}</dd>
          <dt>入社日</dt>
          <dd>{member.hired_on ?? NOT_REGISTERED}</dd>
          <dt>雇用区分</dt>
          <dd>{employmentTypeLabel(member.employment_type)}</dd>
        </dl>
      </Card>

      {!isDeactivated ? (
        <Card title="ログインのしかた">
          {member.phone === null ? (
            <ResultBanner
              tone="error"
              message="電話番号が登録されていないため、この方はログインできません。"
              nextStep={
                canImport
                  ? '「CSVでまとめて登録」で、この方の電話番号を登録してください。'
                  : `${wording.who(ROLE_REQUIREMENTS.rosterImport)}へ、CSVでこの方の電話番号を登録していただくようご依頼ください。`
              }
            />
          ) : (
            <p>
              {member.status === 'invited' ? 'この方はまだログインしていません。' : null}
              ご本人がアプリを開いて、メンバーに登録された電話番号（{formatPhone(member.phone)}
              ）を入れると、SMSで届く認証番号でログインできます。
            </p>
          )}
          {member.status === 'invited' ? (
            <p className="admin-note">
              招待リンクは使わなくなりました。ご本人へは「アプリを開いて、ご自分の電話番号を入れてください」とお伝えください。
            </p>
          ) : null}
        </Card>
      ) : null}

      <Card
        title="ふりがな"
        description="アプリで人を選ぶ一覧で、ふりがなでも探せて、ふりがなの順に並びます。アプリの画面には出ません。"
      >
        {nameKanaBlockedReason !== null ? (
          // 保存して初めて断られる入力欄は出さず、理由の言葉で出す（決定22）
          <p>{nameKanaBlockedReason}</p>
        ) : (
          <FormCard
            bare
            submitLabel="ふりがなを保存する"
            submitBusyLabel="保存しています…"
            busy={savingNameKana}
            onSubmit={() => void saveNameKana()}
            footer={nameKanaResult ? <ResultBanner {...nameKanaResult} /> : undefined}
          >
            <label className="ui-field" htmlFor="member-name-kana">
              <span>ふりがな（ひらがな・カタカナどちらでもかまいません）</span>
              <input
                id="member-name-kana"
                type="text"
                value={nameKana}
                onChange={(e) => setNameKana(e.target.value)}
              />
            </label>
            {nameKanaChanged ? (
              <p className="admin-note">
                {nameKana.trim() === ''
                  ? 'まだ保存していません。空のまま「ふりがなを保存する」を押すと、ふりがなが消えます。'
                  : 'まだ保存していません。「ふりがなを保存する」を押すとメンバーの登録内容が変わります。'}
              </p>
            ) : null}
          </FormCard>
        )}
      </Card>

      <Card
        title="役職と拠点"
        description="役職はCSVでは変わりません。変更はこの画面から1人ずつ行います。"
      >
        {canAssign && roleBeyondOwn.length > 0 ? (
          // 保存して初めて断られる入力欄は出さず、頼み先を言葉で出す（#451・決定22）
          <>
            <p>{BEYOND_OWN_ASSIGNMENT_NOTE}</p>
            <p className="admin-note">
              {`この方の役職（${member.role_display_name}）には、あなたの役職に無い「できること」（${roleBeyondOwn.join('、')}）が含まれるためです。`}
            </p>
          </>
        ) : canAssign && choices ? (
          <FormCard
            bare
            submitLabel="保存する"
            submitBusyLabel="保存しています…"
            busy={savingAssignment}
            onSubmit={() => {
              if (siteId !== (member.site_id ?? '')) setConfirming('assignment');
              else void saveAssignment();
            }}
            footer={assignmentResult ? <ResultBanner {...assignmentResult} /> : undefined}
          >
            <label className="ui-field" htmlFor="member-role">
              <span>役職</span>
              <select id="member-role" value={role} onChange={(e) => setRole(e.target.value)}>
                {choices?.assignable_roles.map((option) => {
                  const unassignable = unassignableRoles.includes(option);
                  return (
                    <option key={option.key} value={option.key} disabled={unassignable}>
                      {unassignable
                        ? `${option.display_name}（付けられません）`
                        : option.display_name}
                    </option>
                  );
                })}
              </select>
            </label>
            {unassignableRoles.length > 0 ? (
              <p className="admin-note">
                {`${unassignableRoles.map((option) => `「${option.display_name}」`).join('')}は、あなたの役職に無い「できること」を含むため、付けられません。`}
              </p>
            ) : null}
            <label className="ui-field" htmlFor="member-site">
              <span>拠点</span>
              <select id="member-site" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                <option value="">決めない</option>
                {choices?.sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </select>
            </label>
            {assignmentChanged ? (
              <p className="admin-note">
                まだ保存していません。「保存する」を押すとメンバーの登録内容が変わります。
              </p>
            ) : null}
          </FormCard>
        ) : canAssign ? (
          <ResultBanner
            tone="error"
            message={withReason(
              choicesError ?? '選択肢を読み込めませんでした',
              '画面を読み込み直してから、もう一度お試しください',
            )}
          />
        ) : isSelf ? (
          <p>{selfAssignmentNote(wording, canAssignOthers)}</p>
        ) : (
          <p>{siteScopeAssignmentNote(wording)}</p>
        )}
      </Card>

      {member.can_have_managed_sites ? (
        <Card
          title="受け持つ拠点"
          description="所属拠点に加えて管理する拠点を選びます。保存すると、その拠点の公式トークとお知らせに参加します。外すと、その拠点のトークから抜けます。以前に手で参加していた場合も抜けます。"
        >
          {member.can_edit_managed_sites && choices ? (
            <FormCard
              bare
              submitLabel="受け持つ拠点を保存する"
              busy={savingManagedSites}
              onSubmit={() => void saveManagedSites()}
              onCancel={() => {
                setManagedSiteIds((member.managed_sites ?? []).map((site) => site.id));
                setManagedSitesResult(null);
              }}
              cancelLabel="元に戻す"
              footer={managedSitesResult ? <ResultBanner {...managedSitesResult} /> : undefined}
            >
              <fieldset disabled={savingManagedSites}>
                <legend>受け持つ拠点</legend>
                {choices.sites.map((site) => (
                  <label key={site.id} className="ui-check">
                    <input
                      type="checkbox"
                      checked={site.id === member.site_id || managedSiteIds.includes(site.id)}
                      disabled={site.id === member.site_id}
                      onChange={(event) =>
                        setManagedSiteIds((ids) =>
                          event.target.checked
                            ? [...ids, site.id]
                            : ids.filter((id) => id !== site.id),
                        )
                      }
                    />
                    {site.name}
                    {site.id === member.site_id ? '（所属拠点のため外せません）' : ''}
                  </label>
                ))}
              </fieldset>
              <p className="admin-note">「受け持つ拠点を保存する」を押すと反映されます。</p>
            </FormCard>
          ) : member.can_edit_managed_sites ? (
            <ResultBanner
              tone="error"
              message={choicesError ?? '拠点を読み込めませんでした。画面を開き直してください。'}
            />
          ) : (
            <>
              <p>
                {[member.site_name, ...(member.managed_sites ?? []).map((site) => site.name)]
                  .filter(Boolean)
                  .join('・') || 'ありません'}
              </p>
              <p>受け持つ拠点の変更は、会社全体を管理できる方にご依頼ください。</p>
            </>
          )}
        </Card>
      ) : null}

      <Card
        title="アカウントを無効にする"
        description="無効にすると、すぐにログインできなくなります。開いたままのアプリも、次にサーバとやりとりしたとき（トークを開く・送る・開き直すなど）にログインの画面に戻り、トークの中身が消えます。辞めた方はここで無効にしてください。"
      >
        {statusResult ? <ResultBanner {...statusResult} /> : null}
        {isSelf && !isDeactivated ? (
          // ご自身は無効にできない（最後の本部管理者がご自身を無効にすると、画面から戻せる方が
          // いなくなるため・issue #370）。押せるのに断られるボタンは出さない
          <p>{selfDeactivateNote(wording, canAssignOthers)}</p>
        ) : statusBlockedReason !== null ? (
          // できない操作はボタンを出さず、理由の言葉でお伝えする（issue #373・決定22）
          <p>{statusBlockedReason}</p>
        ) : (
          <div className="ui-form-actions">
            {isDeactivated ? (
              <Button
                variant="secondary"
                busy={busy && confirming === 'reactivate'}
                onClick={() => setConfirming('reactivate')}
              >
                無効を解除する
              </Button>
            ) : (
              <Button
                variant="danger"
                busy={busy && confirming === 'deactivate'}
                onClick={() => setConfirming('deactivate')}
              >
                アカウントを無効にする
              </Button>
            )}
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={confirming === 'assignment'}
        title="拠点を変更します"
        message={`${member.display_name}さんの拠点を「${choices?.sites.find((site) => site.id === siteId)?.name ?? '決めない'}」に変更します。役職や相手の拠点によっては、前の拠点の方との1対1でメッセージを送れなくなります。これまでのやりとりは読めます。`}
        reversibility="拠点は、この画面でもう一度変更できます。"
        reversible
        tone="primary"
        confirmLabel="変更する"
        busy={savingAssignment}
        onConfirm={() => void saveAssignment()}
        onCancel={() => setConfirming('none')}
      />

      <ConfirmDialog
        open={confirming === 'deactivate'}
        title="アカウントを無効にします"
        message={`${member.display_name}さんのアカウントを無効にします。すぐにログインできなくなります。ご本人のアプリも、次にサーバとやりとりしたとき（トークを開く・送る・開き直すなど）にログインの画面に戻り、トークの中身が消えます。`}
        reversibility="この画面の「無効を解除する」で戻せます（戻したあとは、ご本人が電話番号を入れればまたログインできます）。"
        reversible
        confirmLabel="無効にする"
        busy={busy}
        onConfirm={() => void changeStatus('deactivate')}
        onCancel={() => setConfirming('none')}
      />

      <ConfirmDialog
        open={confirming === 'reactivate'}
        title="無効を解除します"
        message={`${member.display_name}さんの無効を解除します。在籍状態は「ログイン前」に戻ります。`}
        reversibility="もう一度無効にすることもできます。"
        reversible
        tone="primary"
        confirmLabel="無効を解除する"
        busy={busy}
        onConfirm={() => void changeStatus('reactivate')}
        onCancel={() => setConfirming('none')}
      />
    </article>
  );
}

/** 登録されていない項目の出し方（かっこで始めると行頭がずれるため「—」にする・総点検 M10）。 */
const NOT_REGISTERED = '—';

/**
 * ご自身のページでは「無効にする」を出さず、代わりに出す案内（issue #370）。
 * 頼み先は、全社の名簿を扱える役職の、その会社での呼び名（issue #480）。
 */
function selfDeactivateNote(wording: RoleWording, others: boolean): string {
  return `ご自身のアカウントは無効にできません。${wording.ask(ROLE_REQUIREMENTS.assignRoles, { others })}`;
}

/** ご自身の役職・拠点は変えられないときの案内（頼み先はその会社の呼び名・issue #480）。 */
function selfAssignmentNote(wording: RoleWording, others: boolean): string {
  return `ご自身の役職・拠点は、この画面からは変えられません。${wording.ask(
    ROLE_REQUIREMENTS.assignRoles,
    // ご自身もその役職なら「ほかの◯◯の方へ」
    { lead: '変更が必要なときは', others },
  )}`;
}

/** この方の役職に、ご自身が持っていないできることが含まれるときの案内（#451）。 */
const BEYOND_OWN_ASSIGNMENT_NOTE = 'この方の役職・拠点は、ほかの管理者の方に変えていただきます。';

/** 受け持ちが自拠点の方への案内（役職の呼び名はその会社のもの・issue #480）。 */
function siteScopeAssignmentNote(wording: RoleWording): string {
  const only = wording.only(ROLE_REQUIREMENTS.assignRoles, '役職・拠点を変えられるのは');
  return wording.names(ROLE_REQUIREMENTS.assignRoles) === null
    ? only
    : `${only}変更が必要なときは、ご依頼ください。`;
}

/** 結果のお知らせ1つ分。 */
interface Banner {
  tone: 'success' | 'error' | 'info';
  message: string;
  nextStep?: string;
}

/** うまくいかなかったときの帯（理由と、次にすることを必ず添える）。 */
function failureBanner(caught: unknown, headline: string): Banner {
  const failure = loadFailure(caught);
  const message = withReason(headline, failure.message);
  if (failure.status === 403) {
    // この画面そのものは使えているので、「この画面は、いまの役職では…」とは言わない（#451）。
    // 断られたのはこの方・この役職についての操作なので、頼み先だけをお伝えする。
    // 理由の文が頼み先まで言っているとき（「…ご依頼ください。」）は重ねない
    return endsWithRequest(failure.message)
      ? { tone: 'error', message }
      : { tone: 'error', message, nextStep: 'ほかの管理者の方にご依頼ください。' };
  }
  return {
    tone: 'error',
    message,
    // 権限が無いときに「時間をおいて」と出さない（総点検 J-08）
    nextStep: nextStepFor(failure.status, failure.retryable).nextStep,
  };
}
